import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowDown } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import Countdown from "./Countdown";

const ease = [0.16, 1, 0.3, 1];

function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className="line-mask">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onOpenVideo }) {
  const wedding = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] w-full overflow-hidden grain bg-ink">
      {/* Video background with parallax */}
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <video
          className="w-full h-full object-cover"
          src={wedding.media.heroVideo}
          poster={wedding.media.heroPoster}
          autoPlay
          loop
          muted
          playsInline
          data-testid="hero-video"
        />
      </motion.div>
      <motion.div className="absolute inset-0 bg-black" style={{ opacity: overlay }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

      {/* Content */}
      <motion.div
        className="relative z-10 h-full flex flex-col justify-center items-center sm:items-start text-center sm:text-left px-6 sm:px-10 max-w-[1400px] mx-auto"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <MaskLine delay={0.5}>
          <span className="overline text-white/70">— Nuestra Boda —</span>
        </MaskLine>

        <h1 className="mt-6 sm:mt-8 text-white font-display tracking-tight leading-[0.86]">
          <MaskLine delay={0.7} className="text-[19vw] sm:text-[15vw] lg:text-[12rem]">
            {wedding.bride}
          </MaskLine>
          <MaskLine delay={0.86} className="italic text-[15vw] sm:text-[12vw] lg:text-[9rem] pl-0 sm:pl-[6vw]">
            &amp; {wedding.groom}
          </MaskLine>
        </h1>

        <motion.p
          className="mt-8 max-w-md mx-auto sm:mx-0 font-body font-light text-white/80 text-base sm:text-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease }}
        >
          {wedding.tagline}
        </motion.p>

        <motion.div
          className="mt-10 sm:mt-12 flex flex-col items-center sm:flex-row sm:items-center gap-8 sm:gap-14"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.55, ease }}
        >
          <Countdown date={wedding.date} light />

          <button
            onClick={onOpenVideo}
            className="group inline-flex items-center gap-4 self-center sm:self-start"
            data-testid="open-save-the-date"
          >
            <span className="relative block w-28 sm:w-36 aspect-video overflow-hidden border border-white/25">
              <img
                src={wedding.media.saveTheDateThumb}
                alt="Save the Date"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
              />
              <span className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                <span className="w-9 h-9 rounded-full bg-white/90 text-ink flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <Play size={13} className="ml-0.5" fill="currentColor" />
                </span>
              </span>
            </span>
            <span className="text-left">
              <span className="overline text-white block">Ver Save the Date</span>
              <span className="text-white/50 text-xs font-light mt-1.5 block">Reproducir video</span>
            </span>
          </button>
        </motion.div>
      </motion.div>

      <motion.a
        href="#ubicacion"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-white transition-colors"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        aria-label="Bajar"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ArrowDown size={20} strokeWidth={1} />
        </motion.span>
      </motion.a>
    </section>
  );
}
